/**
 * PreDeparturePhase - Phase 1 of voyage management
 */

import React from 'react';
import { MapPin, Users, FileText, Cloud, CheckCircle, Anchor } from 'lucide-react';
import RegulationPreDepartureChecklist from '../RegulationPreDepartureChecklist';

const PreDeparturePhase = ({ 
  activeVoyage,
  phaseCompletion,
  setPhaseCompletion,
  onLetGo,
  canLetGo
}) => {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Pre-Departure Phase</h3>
          <p className="text-sm text-gray-500 mt-1">Complete all requirements before letting go</p>
        </div>
        
        <div className="p-5 space-y-4">
          {/* Voyage Details Section */}
          <div className={`p-4 rounded-lg border-2 ${
            phaseCompletion.voyageDetails ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-gray-600" />
                <div>
                  <h4 className="text-sm font-medium">Voyage Details</h4>
                  {activeVoyage ? (
                    <p className="text-xs text-gray-500">
                      {activeVoyage.departurePort} → {activeVoyage.destinationPort}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">Not yet configured</p>
                  )}
                </div>
              </div>
              {phaseCompletion.voyageDetails && (
                <CheckCircle className="h-5 w-5 text-green-600" />
              )}
            </div>
          </div>

          {/* Crew Assignment Section */}
          <div className={`p-4 rounded-lg border-2 ${
            phaseCompletion.crewAssigned ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-gray-600" />
                <div>
                  <h4 className="text-sm font-medium">Crew Assignment</h4>
                  <p className="text-xs text-gray-500">
                    {activeVoyage?.crewList?.length || 0} crew members assigned
                  </p>
                </div>
              </div>
              {phaseCompletion.crewAssigned && (
                <CheckCircle className="h-5 w-5 text-green-600" />
              )}
            </div>
          </div>

          {/* Pre-Departure Checks */}
          <div className={`p-4 rounded-lg border-2 ${
            phaseCompletion.preDepartureChecks ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-gray-600" />
                <div>
                  <h4 className="text-sm font-medium">Pre-Departure Checks</h4>
                  <p className="text-xs text-gray-500">
                    {phaseCompletion.preDepartureChecks ? 'Completed' : 'Pending'}
                  </p>
                </div>
              </div>
              {phaseCompletion.preDepartureChecks && (
                <CheckCircle className="h-5 w-5 text-green-600" />
              )}
            </div>
          </div>

          {/* Weather Assessment */}
          <div className={`p-4 rounded-lg border-2 ${
            phaseCompletion.weatherAssessed ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Cloud className="h-5 w-5 text-gray-600" />
                <div>
                  <h4 className="text-sm font-medium">Weather Assessment</h4>
                  <p className="text-xs text-gray-500">
                    {phaseCompletion.weatherAssessed ? 'Reviewed' : 'Required'}
                  </p>
                </div>
              </div>
              {phaseCompletion.weatherAssessed && (
                <CheckCircle className="h-5 w-5 text-green-600" />
              )}
            </div>
          </div>

          {/* Let Go Button */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={onLetGo}
              disabled={!canLetGo}
              className={`px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${
                canLetGo
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              <Anchor className="h-5 w-5" />
              Let Go - Depart
            </button>
          </div>
        </div>
      </div>

      {/* Show pre-departure checklist component if voyage exists */}
      {activeVoyage && (
        <RegulationPreDepartureChecklist
          voyageId={activeVoyage.id}
          voyageDetails={activeVoyage}
          onComplete={(data, summary) => {
            setPhaseCompletion(prev => ({
              ...prev,
              preDepartureChecks: true,
              weatherAssessed: !!summary.weather,
              voyageDetails: true,
              crewAssigned: activeVoyage.crewList?.length > 0
            }));
          }}
        />
      )}
    </div>
  );
};

export default PreDeparturePhase;